import { useState, useCallback, useEffect, useRef } from 'react';
import { IoArrowBack } from 'react-icons/io5';
import { RiskCalculator, InputResult, UserAgreementModal, getUserAgreed } from '@/components';
import { Home } from '@/views/Home';
import { SelectionScreen } from '@/views/SelectionScreen';
import { BmiCalculator } from '@/views/BmiCalculator';
import { FindRiskCalculator } from '@/views/FindRiskCalculator';
import { FindRiskResultView } from '@/views/FindRiskResult';
import { Strings } from '@/constants/strings';
import type { UserMode } from '@/components/RiskCalculator';
import type { CalculationResult } from '@/data/RiskCalculator';
import type { FindRiskResult } from '@/data/FindRisk';

type Screen = 'home' | 'selection' | 'calculator' | 'result' | 'bmi' | 'findrisk' | 'findriskResult';

interface HistoryState {
  screen: Screen;
  userMode?: UserMode;
}

function App() {
  const [userAgreed, setUserAgreed] = useState(false);
  const [agreedChecked, setAgreedChecked] = useState(false);
  const isPopState = useRef(false);

  useEffect(() => {
    setUserAgreed(getUserAgreed());
    setAgreedChecked(true);
  }, []);

  const [screen, setScreen] = useState<Screen>('home');
  const [userMode, setUserMode] = useState<UserMode>('healthcare');
  const [result, setResult] = useState<CalculationResult | null>(null);
  const [findRiskResult, setFindRiskResult] = useState<FindRiskResult | null>(null);
  const [lastCalculatedBmi, setLastCalculatedBmi] = useState<number | null>(null);

  useEffect(() => {
    window.history.replaceState({ screen: 'home' } as HistoryState, '', window.location.pathname || '/');
  }, []);

  useEffect(() => {
    const onPopState = (e: PopStateEvent) => {
      isPopState.current = true;
      const s = e.state as HistoryState | null;
      const next = s?.screen ?? 'home';
      setScreen(next);
      if (s?.userMode) setUserMode(s.userMode);
      if (next === 'selection' || next === 'home') {
        setResult(null);
        setFindRiskResult(null);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const pushHistory = useCallback((state: HistoryState) => {
    if (!isPopState.current) {
      window.history.pushState(state, '', window.location.pathname || '/');
    }
    isPopState.current = false;
  }, []);

  const handleSelectMode = useCallback((mode: UserMode) => {
    isPopState.current = false;
    setUserMode(mode);
    setScreen('selection');
    pushHistory({ screen: 'selection', userMode: mode });
  }, [pushHistory]);

  const handleBack = useCallback(() => {
    if (screen === 'findriskResult') {
      setScreen('findrisk');
      setFindRiskResult(null);
      return;
    }
    window.history.back();
  }, [screen]);

  const handleSelectRisk = useCallback(() => {
    isPopState.current = false;
    setScreen('calculator');
    pushHistory({ screen: 'calculator', userMode });
  }, [userMode, pushHistory]);

  const handleSelectBmi = useCallback(() => {
    isPopState.current = false;
    setScreen('bmi');
    pushHistory({ screen: 'bmi', userMode });
  }, [userMode, pushHistory]);

  const handleSelectFindRisk = useCallback(() => {
    isPopState.current = false;
    setScreen('findrisk');
    pushHistory({ screen: 'findrisk', userMode });
  }, [userMode, pushHistory]);

  const handleFindRiskResult = useCallback((r: FindRiskResult) => {
    setFindRiskResult(r);
    setScreen('findriskResult');
  }, []);

  const handleFindRiskNewCalculation = useCallback(() => {
    setFindRiskResult(null);
    setScreen('findrisk');
  }, []);

  const handleBmiCalculated = useCallback((bmi: number) => {
    setLastCalculatedBmi(bmi);
  }, []);

  const handleResult = useCallback((r: CalculationResult) => {
    setResult(r);
    setScreen('result');
  }, []);

  const handleNewCalculation = useCallback(() => {
    setResult(null);
    setScreen('calculator');
  }, []);

  const showBackButton =
    screen === 'selection' ||
    screen === 'calculator' ||
    screen === 'result' ||
    screen === 'bmi' ||
    screen === 'findrisk' ||
    screen === 'findriskResult';

  const headerTitle =
    screen === 'selection'
      ? Strings.selectionTitle
      : screen === 'bmi'
        ? Strings.bmiTitle
        : screen === 'findrisk' || screen === 'findriskResult'
          ? Strings.findRiskTitle
          : Strings.appName;

  const showUserAgreement = !agreedChecked || !userAgreed;

  return (
    <div className="min-h-screen bg-surface text-text-heading">
      <header
        className="sticky top-0 z-40 flex items-center border-b border-slate-200/80 bg-primary px-2 py-3 sm:px-4"
        style={{
          paddingTop: 'max(0.75rem, env(safe-area-inset-top))',
          paddingLeft: 'max(0.5rem, env(safe-area-inset-left))',
          paddingRight: 'max(0.5rem, env(safe-area-inset-right))',
        }}
      >
        <div className="flex min-w-[44px] shrink-0 items-center justify-start">
          {showBackButton && (
            <button
              type="button"
              onClick={handleBack}
              className="touch-target flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-md text-white/95 transition active:bg-white/20 hover:bg-white/15 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-primary"
              aria-label="Geri dön"
            >
              <IoArrowBack className="h-6 w-6 shrink-0 sm:h-7 sm:w-7" aria-hidden />
            </button>
          )}
        </div>
        <h1 className="min-w-0 flex-1 truncate px-2 text-center text-base font-bold text-[#FFFFFF] sm:text-lg">
          {headerTitle}
        </h1>
        <div className="min-w-[44px] shrink-0" aria-hidden />
      </header>

      <main className="flex flex-1 flex-col">
        {screen === 'home' && <Home onSelectMode={handleSelectMode} />}
        {screen === 'selection' && (
          <SelectionScreen
            userMode={userMode}
            onSelectRisk={handleSelectRisk}
            onSelectBmi={handleSelectBmi}
            onSelectFindRisk={handleSelectFindRisk}
          />
        )}
        {screen === 'calculator' && (
          <RiskCalculator userMode={userMode} onResult={handleResult} />
        )}
        {screen === 'result' && result && (
          <InputResult
            result={result}
            userMode={userMode}
            onNewCalculation={handleNewCalculation}
          />
        )}
        {screen === 'bmi' && <BmiCalculator onBmiCalculated={handleBmiCalculated} />}
        {screen === 'findrisk' && (
          <FindRiskCalculator
            initialBmi={lastCalculatedBmi}
            onResult={handleFindRiskResult}
          />
        )}
        {screen === 'findriskResult' && findRiskResult && (
          <FindRiskResultView
            result={findRiskResult}
            userMode={userMode}
            onNewCalculation={handleFindRiskNewCalculation}
          />
        )}
      </main>

      {showUserAgreement && (
        <UserAgreementModal
          visible
          onAccept={() => setUserAgreed(true)}
        />
      )}
    </div>
  );
}

export default App;
